import { type MetaEvent, trackMetaEvent } from './metaPixel'

/**
 * Eventos de conversión para Umami Analytics (y su reenvío al píxel de Meta).
 *
 * <p>Umami (Umami Cloud, región UE; sustituye a Plausible desde sep-2026) se inyecta
 * SOLO en producción (`matchplay.es` / `www`) — ver el snippet en `index.html`. La
 * función global `window.umami` solo existe en ese entorno (y una vez cargado el
 * script); en dev, staging, la app nativa y los tests es `undefined`, así que
 * `trackEvent` es no-op fuera de prod. Por eso NO hace falta gatear por entorno
 * aquí: el optional-chaining (`?.`) ya lo hace.</p>
 *
 * <p>Los nombres de evento son estables (los mismos que se usaban en Plausible):
 * cambiarlos parte la serie histórica y los embudos definidos en el panel.</p>
 */
declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, string | number | boolean>) => void
    }
  }
}

/**
 * Eventos registrados. Umami sí desglosa propiedades (`props`) y tiene embudos,
 * pero los pasos del asistente siguen siendo eventos separados
 * ({@code Session Create Step 1..4}) para no partir la serie ni el embudo.
 *
 * <p>{@code Hero CTA Explore} / {@code Hero CTA Register}: clicks en los botones
 * del hero de la landing. Señal proximal y atribuible para iterar el copy del
 * hero y medir su efecto antes/después de un cambio (ver
 * `docs/frontend/hero-copy-iteration.md`).</p>
 *
 * <p>{@code Play Intent Create} / {@code Join} / {@code Convert}: "Ganas de jugar"
 * — declarar unas ganas, sumarse a un grupo ("Yo también") y pulsar "Crear la
 * partida" desde un grupo (spec 2026-09-26-ganas-de-jugar, R16).</p>
 */
export type AnalyticsEvent =
  | 'Signup'
  | 'Session Created'
  | 'Session Joined'
  | 'Session Create Step 1'
  | 'Session Create Step 2'
  | 'Session Create Step 3'
  | 'Session Create Step 4'
  | 'Session Shared'
  | 'Hero CTA Explore'
  | 'Hero CTA Register'
  /** CTA «Tengo ganas de jugar a…» del hero cuando no hay mesas abiertas (portada honesta). */
  | 'Hero CTA Intent'
  | 'Play Intent Create'
  | 'Play Intent Join'
  | 'Play Intent Convert'
  | 'App Download Click'

/**
 * Qué conversiones se reenvían al píxel de Meta y con qué nombre. Solo las que
 * sirven para optimizar campañas; los CTA del hero se quedan en Umami.
 * `CompleteRegistration` es evento estándar de Meta; el resto, personalizados.
 */
export const META_EVENTS: Partial<Record<AnalyticsEvent, MetaEvent>> = {
  Signup: { name: 'CompleteRegistration', custom: false },
  'Session Created': { name: 'SessionCreated', custom: true },
  'Session Joined': { name: 'SessionJoined', custom: true },
}

/**
 * Único punto de conversión: envía a Umami y, si el evento está mapeado en
 * {@link META_EVENTS}, al píxel de Meta. Ambos son no-op fuera de prod (y el
 * píxel, además, sin consentimiento de publicidad).
 */
export function trackEvent(
  event: AnalyticsEvent,
  props?: Record<string, string | number | boolean>,
): void {
  window.umami?.track(event, props)
  const meta = META_EVENTS[event]
  if (meta) trackMetaEvent(meta)
}
